import { ExternalLink, Monitor, X } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@staff-app/ui';

/**
 * Remote viewer — port of scio3 `RemoteControlDialog`: the one-shot VNC viewer link in a near-full-screen
 * iframe, plus "Open in new tab" for when the viewer won't render framed (or you want it beside the
 * drawer). The link is single-use and short-lived, so it is never persisted.
 */
export function TestingRemoteControlDialog({
  open,
  deviceName,
  remoteUrl,
  onClose,
}: {
  open: boolean;
  deviceName: string;
  remoteUrl: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 p-0"
        aria-describedby={undefined}
        hideCloseButton
        data-testid="testing-remote-control-dialog"
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-2 border-b px-4 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Monitor className="h-5 w-5 shrink-0 text-muted-foreground" />
            <DialogTitle className="truncate text-base">{`Remote Control — ${deviceName}`}</DialogTitle>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {remoteUrl ? (
              <Button variant="outline" size="sm" className="gap-1.5" asChild>
                <a
                  href={remoteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="testing-remote-open-tab"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  {'Open in new tab'}
                </a>
              </Button>
            ) : null}
            <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={'Close'}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {remoteUrl ? (
          <iframe
            src={remoteUrl}
            title={'Remote Control'}
            className="min-h-0 flex-1 border-0"
            allow="clipboard-read; clipboard-write"
          />
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center text-muted-foreground">
            {'No remote control URL available'}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
